/**
 * BoltVoiceInput - Voice input button for BOLT chat interface
 *
 * Uses Web Speech API (SpeechRecognition / webkitSpeechRecognition).
 * Click-and-hold to record, release to stop.
 * Transcribed text is returned via onTranscript callback (NOT auto-sent).
 * Detects browser locale for SpeechRecognition.lang (en-US / es-ES).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { logger } from '../../../utils/logger';
import './BoltVoiceInput.css';

const LOCALE_MAP = {
  en: 'en-US',
  es: 'es-ES',
};

const STRINGS = {
  'en-US': {
    holdToRecord: 'Hold to speak',
    recording: 'Recording…',
    notSupported: 'Voice input is not supported in this browser. Please use Chrome, Edge, or Safari.',
    permissionDenied: 'Microphone access denied. Please enable it in browser settings.',
    noSpeech: 'No speech detected. Try again.',
    networkError: 'Network error. Check your connection.',
    audioCapture: 'Microphone not available. Check permissions.',
    genericError: 'Voice error',
  },
  'es-ES': {
    holdToRecord: 'Mantén para hablar',
    recording: 'Grabando…',
    notSupported: 'La entrada de voz no es compatible con este navegador. Usa Chrome, Edge o Safari.',
    permissionDenied: 'Acceso al micrófono denegado. Habilítalo en la configuración del navegador.',
    noSpeech: 'No se detectó voz. Inténtalo de nuevo.',
    networkError: 'Error de red. Verifica tu conexión.',
    audioCapture: 'Micrófono no disponible. Verifica los permisos.',
    genericError: 'Error de voz',
  },
};

function detectLocale() {
  const lang = (navigator.language || 'en').split('-')[0];
  return LOCALE_MAP[lang] || 'en-US';
}

function getStrings(locale) {
  return STRINGS[locale] || STRINGS['en-US'];
}

const SpeechRecognitionAPI =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

export default function BoltVoiceInput({ onTranscript, disabled = false }) {
  const locale = detectLocale();
  const t = getStrings(locale);

  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState(null);
  const [isSupported] = useState(() => !!SpeechRecognitionAPI);
  const [permissionGranted, setPermissionGranted] = useState(null);
  const recognitionRef = useRef(null);
  const isHoldingRef = useRef(false);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch (_) { /* cleanup */ }
        recognitionRef.current = null;
      }
    };
  }, []);

  const requestMicPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setPermissionGranted(true);
      return true;
    } catch (err) {
      logger.error('Microphone permission denied', { error: err.message });
      setPermissionGranted(false);
      setError(t.permissionDenied);
      return false;
    }
  }, [t]);

  const startRecording = useCallback(async () => {
    if (!isSupported || disabled) return;
    setError(null);

    if (permissionGranted === null || permissionGranted === false) {
      const granted = await requestMicPermission();
      if (!granted) return;
    }

    try {
      const recognition = new SpeechRecognitionAPI();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = locale;

      recognition.onresult = (event) => {
        const text = event.results[0]?.[0]?.transcript;
        if (text && onTranscript) {
          onTranscript(text);
        }
      };

      recognition.onerror = (event) => {
        const code = event.error;
        logger.error('Speech recognition error', { error: code });

        const errorMessages = {
          'not-allowed': t.permissionDenied,
          'no-speech': t.noSpeech,
          'network': t.networkError,
          'audio-capture': t.audioCapture,
        };
        setError(errorMessages[code] || `${t.genericError}: ${code}`);
        setIsRecording(false);
        isHoldingRef.current = false;
      };

      recognition.onend = () => {
        setIsRecording(false);
        recognitionRef.current = null;
      };

      recognition.onstart = () => {
        setIsRecording(true);
        setError(null);
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      logger.error('Failed to start speech recognition', { error: err.message });
      setError(t.genericError);
      setIsRecording(false);
    }
  }, [isSupported, disabled, permissionGranted, locale, onTranscript, requestMicPermission, t]);

  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (_) { /* already stopped */ }
    }
    setIsRecording(false);
  }, []);

  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    isHoldingRef.current = true;
    startRecording();
  }, [startRecording]);

  const handlePointerUp = useCallback((e) => {
    e.preventDefault();
    if (isHoldingRef.current) {
      isHoldingRef.current = false;
      stopRecording();
    }
  }, [stopRecording]);

  const handlePointerLeave = useCallback((e) => {
    e.preventDefault();
    if (isHoldingRef.current) {
      isHoldingRef.current = false;
      stopRecording();
    }
  }, [stopRecording]);

  const handleKeyDown = useCallback((e) => {
    if ((e.key === ' ' || e.key === 'Enter') && !isHoldingRef.current) {
      e.preventDefault();
      isHoldingRef.current = true;
      startRecording();
    }
  }, [startRecording]);

  const handleKeyUp = useCallback((e) => {
    if ((e.key === ' ' || e.key === 'Enter') && isHoldingRef.current) {
      e.preventDefault();
      isHoldingRef.current = false;
      stopRecording();
    }
  }, [stopRecording]);

  if (!isSupported) {
    return (
      <div className="bolt-voice-input">
        <button
          className="bolt-voice-btn bolt-voice-unsupported"
          disabled
          aria-label={t.notSupported}
          title={t.notSupported}
          type="button"
        >
          <MicOff size={18} />
        </button>
      </div>
    );
  }

  return (
    <div className="bolt-voice-input">
      <button
        className={`bolt-voice-btn ${isRecording ? 'bolt-voice-recording' : ''} ${error ? 'bolt-voice-error' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
        onPointerCancel={handlePointerLeave}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        disabled={disabled}
        aria-label={isRecording ? t.recording : t.holdToRecord}
        aria-pressed={isRecording}
        title={isRecording ? t.recording : t.holdToRecord}
        type="button"
      >
        {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
        {isRecording && (
          <span className="bolt-voice-pulse-ring" aria-hidden="true" />
        )}
        {isRecording && (
          <span className="bolt-voice-waveform" aria-hidden="true">
            <span className="bolt-wave-bar" />
            <span className="bolt-wave-bar" />
            <span className="bolt-wave-bar" />
            <span className="bolt-wave-bar" />
            <span className="bolt-wave-bar" />
          </span>
        )}
      </button>

      {error && (
        <div className="bolt-voice-error-msg" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
